import React, { createContext, PropsWithChildren, useContext } from "react";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/design-system/components/Button/Button";
import {
  DrawerContent,
  DrawerCtaSection,
} from "@/design-system/components/Drawer/Drawer";
import { Typography } from "@/design-system/components/Typography/Typography";

type WizardStepFrame = {
  stepNumber: number;
  stepCount: number;
  canGoBack: boolean;
  goBack: () => void;
  goNext: () => void;
};

export const WizardStepFrameContext = createContext<WizardStepFrame | null>(
  null,
);

function useWizardStepFrame(): WizardStepFrame {
  const frame = useContext(WizardStepFrameContext);
  if (!frame) {
    throw new Error("WizardStepLayout must be rendered inside a wizard panel");
  }
  return frame;
}

type PrimaryAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  disabledReason?: string;
};

type WizardStepLayoutProps = PropsWithChildren<{
  title: string;
  description?: React.ReactNode;
  primaryAction: PrimaryAction;
  /** Renders a "Skip for now" button that advances without the primary action. */
  skippable?: boolean;
  secondaryActions?: React.ReactNode;
}>;

export function WizardStepLayout({
  title,
  description,
  primaryAction,
  skippable = false,
  secondaryActions,
  children,
}: WizardStepLayoutProps) {
  const { stepNumber, stepCount, canGoBack, goBack, goNext } =
    useWizardStepFrame();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DrawerContent className="py-8">
        <div className="animate-step-in mx-auto flex max-w-3xl flex-col gap-6">
          <div className="flex flex-col gap-1">
            <Typography variant="eyebrow-sm">
              Step {stepNumber} of {stepCount}
            </Typography>
            <Typography variant="heading-lg" as="h2">
              {title}
            </Typography>
            {description && (
              <Typography variant="body-md" color="subtle">
                {description}
              </Typography>
            )}
          </div>
          {children}
        </div>
      </DrawerContent>
      <DrawerCtaSection className="flex items-center justify-between gap-4">
        <div>
          {canGoBack && (
            <Button
              variant="ghost"
              leadingIcon={<ArrowLeft aria-hidden />}
              onClick={goBack}
            >
              Back
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {secondaryActions}
          {skippable && (
            <Button variant="outline" onClick={goNext}>
              Skip for now
            </Button>
          )}
          <Button
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            loading={primaryAction.loading}
            tooltipContent={
              primaryAction.disabled ? primaryAction.disabledReason : undefined
            }
          >
            {primaryAction.label}
          </Button>
        </div>
      </DrawerCtaSection>
    </div>
  );
}
